/**
 * v1.14.70.0 — AI Site Doctor client (FASE 7A).
 *
 * - Correlation ID stored in sessionStorage
 * - Action buffer (ring 5 events) for context
 * - Throttle by signature (1 per second)
 * - Fetch + axios + window.onerror + console.error + DOM observer
 */
import { sanitizePayload } from "./clientSanitizer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_CORR = "colucci_correlation_id";
const ACTION_BUFFER_SIZE = 5;
const FRONTEND_VERSION = "v1.14.75.0-ai-site-doctor-selftest-rollback";

let _lastSentBySig = new Map();
let _actionBuffer = [];
let _installed = false;

export function getCorrelationId() {
  let cid = sessionStorage.getItem(STORAGE_CORR);
  if (!cid) {
    cid = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
      });
    sessionStorage.setItem(STORAGE_CORR, cid);
  }
  return cid;
}

export function pushAction(action) {
  try {
    _actionBuffer.push({ ...sanitizePayload(action),
                          ts: new Date().toISOString() });
    if (_actionBuffer.length > ACTION_BUFFER_SIZE) _actionBuffer.shift();
  } catch (_) { /* ignore */ }
}

function _signature(payload) {
  return `${payload.source || "u"}|${(payload.error_message || "").slice(0, 80)}|${payload.component_name || ""}|${payload.api_endpoint || ""}`;
}

export async function reportError(payload) {
  try {
    const sig = _signature(payload);
    const now = Date.now();
    const last = _lastSentBySig.get(sig) || 0;
    if (now - last < 1000) return null; // throttle 1/sec/signature
    _lastSentBySig.set(sig, now);

    const sanitized = sanitizePayload({
      ...payload,
      correlation_id: payload.correlation_id || getCorrelationId(),
      action_before_error: _actionBuffer.slice(),
      page_url: payload.page_url || window.location.pathname,
      browser: navigator.userAgent.substring(0, 200),
      frontend_version: FRONTEND_VERSION,
    });

    // Fire-and-forget; non bloccante
    fetch(`${API}/ai-site-doctor/report-error`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Correlation-Id": sanitized.correlation_id,
      },
      body: JSON.stringify(sanitized),
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch (_) { /* never break the host page */ }
  return null;
}

/* ---------- DOM red message observer ---------- */
const RED_CLASS_RE = /\b(text-red-|bg-red-|border-red-|alert-danger|error|validation-error|form-error)\b/;
let _redObserverThrottle = new Map();

function _checkRedNode(node) {
  if (!(node instanceof Element)) return;
  const cls = node.className || "";
  if (typeof cls !== "string") return;
  if (!RED_CLASS_RE.test(cls)) return;
  const txt = (node.textContent || "").trim();
  if (!txt || txt.length > 500) return;
  const path = window.location.pathname;
  const key = `${path}|${txt.slice(0, 60)}`;
  const now = Date.now();
  const bucket = _redObserverThrottle.get(path) || [];
  // Pulisci entries vecchie (>60s)
  const recent = bucket.filter((t) => now - t < 60000);
  if (recent.length >= 5) return;
  recent.push(now);
  _redObserverThrottle.set(path, recent);

  reportError({
    source: "frontend",
    severity: "medium",
    error_message: `[DOM red message] ${txt}`,
    dom_error_text: txt,
    component_name: node.tagName + "." + (node.className || "").split(" ").slice(0, 2).join("."),
    page_url: path,
  });
}

function _installDomObserver() {
  if (!document.body) return;
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((n) => {
        _checkRedNode(n);
        if (n.querySelectorAll) {
          n.querySelectorAll('[class*="text-red"], [class*="bg-red"], [class*="border-red"], .error, .alert-danger, .validation-error, .form-error')
            .forEach(_checkRedNode);
        }
      });
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

/* ---------- console.error wrapper ---------- */
function _installConsoleErrorWrapper() {
  const orig = console.error;
  console.error = (...args) => {
    try {
      const msg = args.map((a) => {
        if (a instanceof Error) return a.stack || a.message;
        if (typeof a === "object") { try { return JSON.stringify(a); } catch (_) { return String(a); } }
        return String(a);
      }).join(" ").slice(0, 1000);
      // Filter rumore: solo se sembra errore reale
      if (/error|exception|failed|TypeError|ReferenceError|SyntaxError|Network/i.test(msg)) {
        reportError({
          source: "frontend",
          severity: "medium",
          error_message: msg.slice(0, 300),
          stack_trace: msg.slice(0, 2000),
          component_name: "console.error",
        });
      }
    } catch (_) { /* ignore */ }
    orig.apply(console, args);
  };
}

/* ---------- window.onerror + unhandledrejection ---------- */
function _installWindowErrorHandlers() {
  window.addEventListener("error", (ev) => {
    reportError({
      source: "frontend",
      severity: "high",
      error_message: ev.message || "window.onerror",
      stack_trace: ev.error?.stack?.slice(0, 2000) || "",
      component_name: ev.filename || "",
    });
  });
  window.addEventListener("unhandledrejection", (ev) => {
    const r = ev.reason;
    reportError({
      source: "frontend",
      severity: "high",
      error_message: (r && (r.message || String(r))).slice(0, 300),
      stack_trace: r?.stack?.slice(0, 2000) || "",
      component_name: "unhandledrejection",
    });
  });
}

/* ---------- fetch wrapper ---------- */
function _installFetchWrapper() {
  const origFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const [url, opts = {}] = args;
    // Inject correlation_id header (skip se è il report-error stesso per non loop)
    const corrId = getCorrelationId();
    const headers = new Headers(opts.headers || {});
    if (!headers.has("X-Correlation-Id")) headers.set("X-Correlation-Id", corrId);
    const newOpts = { ...opts, headers };
    let res;
    try {
      res = await origFetch(url, newOpts);
    } catch (e) {
      // network error
      reportError({
        source: "api",
        severity: "high",
        error_message: `Network error: ${e.message}`,
        api_endpoint: typeof url === "string" ? url : url.url,
        api_method: (opts.method || "GET").toUpperCase(),
      });
      throw e;
    }
    if (res.status >= 400 && !String(url).includes("/ai-site-doctor/")) {
      // Catch 4xx/5xx (skip report-error itself)
      try {
        const body = await res.clone().json().catch(() => ({}));
        reportError({
          source: "api",
          severity: res.status >= 500 ? "critical" : "medium",
          error_message: `HTTP ${res.status} on ${url}`,
          api_endpoint: typeof url === "string" ? url : url.url,
          api_method: (opts.method || "GET").toUpperCase(),
          api_status_code: res.status,
          response_payload_sanitized: body,
        });
      } catch (_) { /* ignore */ }
    }
    return res;
  };
}

/* ---------- pageview tracker ---------- */
function _installPageviewTracker() {
  const push = () => pushAction({
    type: "pageview", route: window.location.pathname,
    search: window.location.search,
  });
  push();
  const origPush = history.pushState;
  history.pushState = function (...args) {
    origPush.apply(this, args);
    push();
  };
  window.addEventListener("popstate", push);
}

/* ---------- click tracker ---------- */
function _installClickTracker() {
  document.addEventListener("click", (ev) => {
    const t = ev.target?.closest?.("[data-testid], button, a, [role=button]");
    if (!t) return;
    pushAction({
      type: "click",
      testid: t.getAttribute("data-testid") || null,
      label: (t.getAttribute("aria-label") || t.textContent || "").trim().slice(0, 60),
      tag: t.tagName,
    });
  }, true);
}

export function installAISiteDoctor() {
  if (_installed) return;
  _installed = true;
  try {
    getCorrelationId(); // ensure exists
    _installWindowErrorHandlers();
    _installConsoleErrorWrapper();
    _installFetchWrapper();
    _installDomObserver();
    _installPageviewTracker();
    _installClickTracker();
    console.info("[AISiteDoctor] v1.14.75.0 collector installed");
  } catch (e) { /* never block app */ }
}
